import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import SocialLinks from '../components/SocialLinks';
import { seo } from '../seo/seoData';
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

const ContactPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact error:', error);
      alert(t('contactPage.form.errorAlert'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="contact-page">
          <Seo {...seo.contact} />
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white border-b border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
                {t('contactPage.badge')}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-black tracking-tight mb-6">
                {t('contactPage.title1')}<br />
                <span className="text-neutral-400">{t('contactPage.title2')}</span>
              </h1>
              <p className="text-lg text-neutral-600 mb-12 max-w-lg leading-relaxed">
                {t('contactPage.description')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-black font-semibold mb-1">{t('contactPage.info.phoneLabel')}</h3>
                    <a href="tel:0939242320" className="text-neutral-600 hover:text-black transition-colors text-lg">
                      09 39 24 23 20
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-black font-semibold mb-1">{t('contactPage.info.whatsappLabel')}</h3>
                    <a
                      href="https://wa.me/33766301503"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-600 hover:text-black transition-colors text-lg"
                    >
                      07 66 30 15 03
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-neutral-700" />
                  </div>
                  <div>
                    <h3 className="text-black font-semibold mb-1">{t('contactPage.info.emailLabel')}</h3>
                    <span className="text-neutral-600 text-lg">contact@blackmatte.fr</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-neutral-700" />
                  </div>
                  <div>
                    <h3 className="text-black font-semibold mb-1">{t('contactPage.info.zoneLabel')}</h3>
                    <span className="text-neutral-600 text-lg">{t('contactPage.info.zoneValue')}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-neutral-700" />
                  </div>
                  <div>
                    <h3 className="text-black font-semibold mb-1">{t('contactPage.info.availabilityLabel')}</h3>
                    <span className="text-neutral-600 text-lg">{t('contactPage.info.availabilityValue')}</span>
                  </div>
                </div>
              </div>

              {/* DPA-4 — official social identities (Suivez-nous). Added below; the phone/email cards above are unchanged. */}
              <div className="mt-10" data-testid="contact-social">
                <h3 className="text-black font-semibold mb-4">Suivez-nous</h3>
                <div className="flex flex-wrap gap-3">
                  <SocialLinks tone="light" />
                </div>
              </div>
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {success ? (
                <div className="bg-neutral-50 border border-neutral-100 rounded-2xl p-12 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-black mb-2">
                    {t('contactPage.success.title')}
                  </h3>
                  <p className="text-neutral-600">{t('contactPage.success.subtitle')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-neutral-50 border border-neutral-100 rounded-2xl p-8 space-y-6">
                  <h2 className="font-heading text-2xl font-bold text-black mb-6">
                    {t('contactPage.form.title')}
                  </h2>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-700">{t('contactPage.form.fullName')}</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('contact.namePlaceholder')}
                      className="bg-white border-neutral-200 text-black placeholder:text-neutral-400 focus:border-black h-12 rounded-xl"
                      required
                      data-testid="contact-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-700">{t('contact.email')}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('contact.emailPlaceholder')}
                      className="bg-white border-neutral-200 text-black placeholder:text-neutral-400 focus:border-black h-12 rounded-xl"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-700">{t('contact.phone')}</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder={t('contact.phonePlaceholder')}
                      className="bg-white border-neutral-200 text-black placeholder:text-neutral-400 focus:border-black h-12 rounded-xl"
                      required
                      data-testid="contact-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-700">{t('contact.message')}</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder={t('contact.messagePlaceholder')}
                      className="bg-white border-neutral-200 text-black placeholder:text-neutral-400 focus:border-black min-h-[150px] rounded-xl"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white hover:bg-neutral-800 py-6 text-base font-semibold rounded-xl shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5"
                    data-testid="contact-submit"
                  >
                    {loading ? t('contact.sending') : t('contact.send')}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-16 bg-[#f8f8f8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="bg-white border border-neutral-100 rounded-2xl p-8 text-center">
            <h3 className="font-heading text-xl font-semibold text-black mb-2">
              {t('contactPage.mapZone.title')}
            </h3>
            <p className="text-neutral-500">
              {t('contactPage.mapZone.subtitle')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
